import {
  buildAttributionCallbackParams,
  getCampaignAttribution,
  isGtiPathname
} from './campaign-attribution';
import { tagAdjustLinkWithCurrentPage } from './adjust-page-tag';

const ADJUST_APP_KINDS = {
  GTG: 'gtg',
  GTI: 'gti'
};

const ADJUST_NAMESPACE_BY_APP = {
  [ADJUST_APP_KINDS.GTG]: 'heygotrade-gtg',
  [ADJUST_APP_KINDS.GTI]: 'heygotrade-gti'
};

const TRACK_EVENT_TIMEOUT_MS = 1500;

let adjustModulePromise = null;
let initializedSignature = null;
let initializationPromise = null;
let pendingSignature = null;

const warnedMessages = new Set();

function warnOnce(key, message, shouldWarn = true) {
  if (!shouldWarn || warnedMessages.has(key)) {
    return;
  }

  warnedMessages.add(key);
}

function canUseBrowser() {
  return typeof window !== 'undefined';
}

function isProductionEnvironment() {
  return process.env.GATSBY_ACTIVE_ENV === 'production' || process.env.NODE_ENV === 'production';
}

function getAdjustEnvironment() {
  return isProductionEnvironment() ? 'production' : 'sandbox';
}

function getAdjustLogLevel() {
  return 'error';
}

function getAdjustAppKind(pathname = '') {
  return isGtiPathname(pathname) ? ADJUST_APP_KINDS.GTI : ADJUST_APP_KINDS.GTG;
}

function getCurrentAdjustAppKind() {
  if (!canUseBrowser()) {
    return ADJUST_APP_KINDS.GTG;
  }

  return getAdjustAppKind(window.location.pathname);
}

function getAdjustAppToken(appKind, environment) {
  if (appKind === ADJUST_APP_KINDS.GTI) {
    return environment === 'production'
      ? process.env.GATSBY_ADJUST_GTI_APP_TOKEN_PRODUCTION
      : process.env.GATSBY_ADJUST_GTI_APP_TOKEN_SANDBOX;
  }

  return environment === 'production'
    ? process.env.GATSBY_ADJUST_GTG_APP_TOKEN_PRODUCTION
    : process.env.GATSBY_ADJUST_GTG_APP_TOKEN_SANDBOX;
}

function getSdkSignature(appKind, environment, appToken) {
  return `${appKind}:${environment}:${appToken}`;
}

function getDownloadClickEventToken(appKind, environment = getAdjustEnvironment()) {
  if (appKind === ADJUST_APP_KINDS.GTI) {
    return environment === 'production'
      ? process.env.GATSBY_ADJUST_GTI_DOWNLOAD_CLICK_EVENT_TOKEN_PRODUCTION ||
          process.env.GATSBY_ADJUST_GTI_DOWNLOAD_CLICK_EVENT_TOKEN
      : process.env.GATSBY_ADJUST_GTI_DOWNLOAD_CLICK_EVENT_TOKEN_SANDBOX ||
          process.env.GATSBY_ADJUST_GTI_DOWNLOAD_CLICK_EVENT_TOKEN;
  }

  return environment === 'production'
    ? process.env.GATSBY_ADJUST_GTG_DOWNLOAD_CLICK_EVENT_TOKEN_PRODUCTION ||
        process.env.GATSBY_ADJUST_GTG_DOWNLOAD_CLICK_EVENT_TOKEN
    : process.env.GATSBY_ADJUST_GTG_DOWNLOAD_CLICK_EVENT_TOKEN_SANDBOX ||
        process.env.GATSBY_ADJUST_GTG_DOWNLOAD_CLICK_EVENT_TOKEN;
}

function navigateToUrl(url, { openInNewTab = false, replace = false } = {}) {
  if (!canUseBrowser() || !url) {
    return;
  }

  if (openInNewTab) {
    window.open(url, '_blank', 'noopener,noreferrer');
    return;
  }

  if (replace) {
    window.location.replace(url);
    return;
  }

  window.location.assign(url);
}

function getDefaultCallbackParams(url, appKind) {
  if (!canUseBrowser()) {
    return [];
  }

  return [
    { key: 'page_path', value: window.location.pathname },
    { key: 'destination_url', value: url },
    { key: 'app_kind', value: appKind },
    // Paid-visit attribution (channel, utm_*, click ids) so the web click event lines up with the
    // link the visitor is sent through; empty on organic visits (SEOENG-38).
    ...buildAttributionCallbackParams(getCampaignAttribution() || {})
  ];
}

async function loadAdjustSdk() {
  if (!canUseBrowser()) {
    return null;
  }

  if (!adjustModulePromise) {
    adjustModulePromise = import('@adjustcom/adjust-web-sdk')
      .then((module) => module.default || module)
      .catch(() => {
        adjustModulePromise = null;
        return null;
      });
  }

  return adjustModulePromise;
}

async function initializeAdjustSdkForAppKind(appKind) {
  const environment = getAdjustEnvironment();
  const appToken = getAdjustAppToken(appKind, environment);

  if (!appToken) {
    warnOnce(
      `missing-app-token:${appKind}:${environment}`,
      `Adjust Web SDK skipped: missing ${appKind.toUpperCase()} ${environment} app token.`,
      !isProductionEnvironment()
    );

    return { initialized: false, reason: 'missing-app-token', appKind };
  }

  const signature = getSdkSignature(appKind, environment, appToken);

  if (initializedSignature === signature) {
    return { initialized: true, appKind };
  }

  if (initializedSignature && initializedSignature !== signature) {
    warnOnce(
      `cross-app-init:${initializedSignature}:${signature}`,
      `Adjust Web SDK already initialized with ${initializedSignature}. A hard reload is required before switching to ${signature}.`
    );

    return { initialized: false, reason: 'different-signature', appKind };
  }

  if (initializationPromise && pendingSignature === signature) {
    return initializationPromise;
  }

  pendingSignature = signature;
  initializationPromise = loadAdjustSdk()
    .then((Adjust) => {
      if (!Adjust) {
        return { initialized: false, reason: 'sdk-load-failed', appKind };
      }

      Adjust.initSdk({
        appToken,
        environment,
        logLevel: getAdjustLogLevel(),
        namespace: ADJUST_NAMESPACE_BY_APP[appKind]
      });

      initializedSignature = signature;

      return { initialized: true, appKind };
    })
    .finally(() => {
      initializationPromise = null;
      pendingSignature = null;
    });

  return initializationPromise;
}

async function initializeAdjustSdk(pathname = canUseBrowser() ? window.location.pathname : '') {
  if (!canUseBrowser()) {
    return { initialized: false, reason: 'server' };
  }

  return initializeAdjustSdkForAppKind(getAdjustAppKind(pathname));
}

async function trackDownloadClick({ url, appKind, callbackParams = [], timeoutMs }) {
  const environment = getAdjustEnvironment();
  const eventToken = getDownloadClickEventToken(appKind, environment);

  await initializeAdjustSdkForAppKind(appKind);

  if (!eventToken) {
    warnOnce(
      `missing-event-token:${appKind}:${environment}`,
      `Adjust download click event skipped: missing ${appKind.toUpperCase()} ${environment} event token.`
    );

    return;
  }

  const Adjust = await loadAdjustSdk();

  if (!Adjust) {
    return;
  }

  try {
    await Promise.race([
      Adjust.trackEvent({
        eventToken,
        callbackParams: [...getDefaultCallbackParams(url, appKind), ...callbackParams]
      }),
      new Promise((_, reject) => {
        setTimeout(() => reject(new Error('Adjust trackEvent timed out')), timeoutMs);
      })
    ]);
  } catch {
    // Ignore tracking failures; navigation must not depend on them.
  }
}

async function trackThenNavigate({
  url: rawUrl,
  appKind = null,
  openInNewTab = false,
  replace = false,
  callbackParams = [],
  placement = '',
  timeoutMs = TRACK_EVENT_TIMEOUT_MS
} = {}) {
  if (!rawUrl || rawUrl === '#' || !canUseBrowser()) {
    return;
  }

  // SEOENG-117: every download exit carries the page as the Adjust adgroup. A static tracker link
  // (store badge, default download link) gains adj_campaign + adj_adgroup=<this page>; short links
  // and links that already carry an adgroup pass through unchanged. A placement (DAR-235 follow-up)
  // adds adj_creative=<placement> on the same static links.
  const url = tagAdjustLinkWithCurrentPage(rawUrl, placement);
  const resolvedAppKind = appKind || getCurrentAdjustAppKind();

  if (openInNewTab) {
    // Open the real URL synchronously, while the tap's user activation is still valid, and
    // track afterwards: this page stays alive, so the event is not lost. Pre-opening a blank
    // tab and pointing it at the URL only after the awaits below made iOS Safari refuse the
    // App Store / universal-link handoff at the end of the Adjust redirect chain (no user
    // activation by then) and strand the new tab on about:blank.
    navigateToUrl(url, { openInNewTab: true });
    await trackDownloadClick({ url, appKind: resolvedAppKind, callbackParams, timeoutMs });
    return;
  }

  // Same-tab navigation unloads this page, so the event has to be flushed (or time out) first.
  await trackDownloadClick({ url, appKind: resolvedAppKind, callbackParams, timeoutMs });
  navigateToUrl(url, { replace });
}

function shouldReloadForAdjustAppChange(prevPathname = '', nextPathname = '') {
  return getAdjustAppKind(prevPathname) !== getAdjustAppKind(nextPathname);
}

async function reinitializeAdjustSdk(pathname) {
  const Adjust = await loadAdjustSdk();

  if (Adjust && typeof Adjust.stopSdk === 'function') {
    Adjust.stopSdk();
  }

  initializedSignature = null;
  initializationPromise = null;
  pendingSignature = null;

  return initializeAdjustSdk(pathname);
}

export {
  ADJUST_APP_KINDS,
  getAdjustAppKind,
  getCurrentAdjustAppKind,
  initializeAdjustSdk,
  initializeAdjustSdkForAppKind,
  reinitializeAdjustSdk,
  shouldReloadForAdjustAppChange,
  trackThenNavigate
};
